/**
 * ESCHER FALLS — 表示文字列。12言語。
 *
 * ## なぜここまで増やしたか
 * 148の国と地域で売っているのに、ストアも中身も日本語と英語しか無かった。
 * この遊びは言葉をほとんど使わない（視点を回して水路を繋ぐ）ので、
 * **訳す量に対して届く範囲が桁違いに広い。**
 *
 * ## 言語の選び方
 * **配信している地域に合わせてある。** EU 27か国は配信から外してあるので、
 * ドイツ語・フランス語・イタリア語は入れていない——訳しても売れる先が無い。
 * 代わりに中国・韓国・台湾・香港・中南米・ブラジル・ロシア・トルコ・
 * インドネシア・タイ・ベトナムを取る。
 *
 * ## 崩れないようにしてある仕掛け
 * 各言語は `Strings`（＝`locales/ja.ts` の形）を満たすことを型で強制される。
 * **片方にだけ足すと型検査で落ちる。** 訳し忘れを目で探さなくてよい。
 *
 * ## 置き場所
 * ここは表示の担当。`game/` の純ロジックは文字列でなく**鍵**を返し、
 * ここで訳す（`rankOf` → `t.rank[key]`）。ロジックが言語を知らずに済む。
 */

import { JA, type Strings } from './locales/ja';
import { EN } from './locales/en';
import { ZH_HANS } from './locales/zhHans';
import { ZH_HANT } from './locales/zhHant';
import { KO } from './locales/ko';
import { ES } from './locales/es';
import { PT } from './locales/pt';
import { RU } from './locales/ru';
import { TR } from './locales/tr';
import { ID } from './locales/id';
import { TH } from './locales/th';
import { VI } from './locales/vi';

export type { Strings };

/** 対応する言語。 */
export type Lang =
  | 'ja'
  | 'en'
  | 'zh-Hans'
  | 'zh-Hant'
  | 'ko'
  | 'es'
  | 'pt'
  | 'ru'
  | 'tr'
  | 'id'
  | 'th'
  | 'vi';

const DICTS: Record<Lang, Strings> = {
  ja: JA,
  en: EN,
  'zh-Hans': ZH_HANS,
  'zh-Hant': ZH_HANT,
  ko: KO,
  es: ES,
  pt: PT,
  ru: RU,
  tr: TR,
  id: ID,
  th: TH,
  vi: VI,
};

/**
 * BCP 47 の札を手持ちの言語に寄せる。
 *
 * **中国語は繁簡の見分けが要る。** `zh-TW` `zh-HK` `zh-MO` と
 * `zh-Hant-*` は繁体字、それ以外の `zh` は簡体字に倒す。
 * ここを一括で `zh` にすると、台湾と香港の人に簡体字が出る。
 */
function match(tag: string): Lang | null {
  const l = tag.toLowerCase();
  if (l.startsWith('ja')) return 'ja';
  if (l.startsWith('zh')) {
    const hant = /hant|-tw|-hk|-mo/.test(l);
    return hant ? 'zh-Hant' : 'zh-Hans';
  }
  if (l.startsWith('ko')) return 'ko';
  if (l.startsWith('es')) return 'es';
  if (l.startsWith('pt')) return 'pt';
  if (l.startsWith('ru')) return 'ru';
  if (l.startsWith('tr')) return 'tr';
  // インドネシア語の古い札は `in`。**`id` だけを見ると取りこぼす。**
  if (l.startsWith('id') || l.startsWith('in')) return 'id';
  if (l.startsWith('th')) return 'th';
  if (l.startsWith('vi')) return 'vi';
  if (l.startsWith('en')) return 'en';
  return null;
}

/** 言語の呼び名。**その言語自身の綴りで書く。** 探している人が読めるように。 */
export const LANG_NAMES: Record<Lang, string> = {
  ja: '日本語',
  en: 'English',
  'zh-Hans': '简体中文',
  'zh-Hant': '繁體中文',
  ko: '한국어',
  es: 'Español',
  pt: 'Português',
  ru: 'Русский',
  tr: 'Türkçe',
  id: 'Bahasa Indonesia',
  th: 'ไทย',
  vi: 'Tiếng Việt',
};

/** 手で選んだ言語の置き場。 */
const PICK_KEY = 'escher-falls/lang/v1';

/**
 * 手で選んだ言語。選んでいなければ null（＝端末に合わせる）。
 *
 * **決して例外を投げない。** 読めなくても端末の設定に落ちればよい
 * （`game/settings.ts` と同じ流儀）。
 */
export function pickedLang(): Lang | null {
  try {
    const v = window.localStorage.getItem(PICK_KEY);
    return v !== null && v in DICTS ? (v as Lang) : null;
  } catch {
    return null;
  }
}

/**
 * 言語を選び直す。**画面を作り直す必要があるので読み込み直す。**
 *
 * 辞書は起動時に1つ決めて全画面が持っていくので、途中で差し替えると
 * 「一部だけ新しい言語」という半端な画面になる。作り直すのが確実である。
 * 同梱構成では手元のファイルを読むだけなので、読み込み直しは一瞬で終わる。
 */
export function setLang(lang: Lang | null): void {
  try {
    if (lang === null) window.localStorage.removeItem(PICK_KEY);
    else window.localStorage.setItem(PICK_KEY, lang);
  } catch {
    // 保存できない環境では、この一回だけ効かない。諦めてよい。
  }
  window.location.reload();
}

/**
 * 使う言語を決める。優先順は
 *   1. `?lang=ko`（開発・確認用。URL が一番強い）
 *   2. 設定で手で選んだ言語
 *   3. ビルド時に決めた既定の言語（`VITE_DEFAULT_LANG`。itch.io 向けの体験版だけが使う）
 *   4. 端末の言語設定（優先順に見て最初に合ったもの）
 *   5. 英語
 *
 * 3 は **置き場所ごとの客層に合わせる**ためにある。itch.io は英語の場所なので、
 * 日本語の端末で開いた人（作者自身を含む）にも英語で見せる。日本語で遊びたい人は
 * 設定（≡）の言語から選び直せる。自分のサイトと PLiCy の体験版、アプリでは使わない。
 *
 * **端末の設定だけに従うのでは足りない。** 台湾の人が英語の端末を使っていれば
 * 英語が出るし、そもそも12言語を実機で見て確かめる手立てが無くなる。
 */
function detectLang(): Lang {
  try {
    const forced = new URLSearchParams(window.location.search).get('lang');
    if (forced !== null && forced in DICTS) return forced as Lang;
    // 短い札でも拾えるようにする（`?lang=zh` → 簡体字）。
    if (forced !== null) {
      const m = match(forced);
      if (m !== null) return m;
    }
  } catch {
    // URL が読めない環境でも既定に落ちればよい。
  }
  const picked = pickedLang();
  if (picked !== null) return picked;

  const fixed = import.meta.env.VITE_DEFAULT_LANG;
  if (typeof fixed === 'string' && fixed in DICTS) return fixed as Lang;

  const tags =
    typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
  for (const tag of tags) {
    if (typeof tag !== 'string') continue;
    const m = match(tag);
    if (m !== null) return m;
  }
  return 'en';
}

export const LANG: Lang = detectLang();

/** いま使う辞書。 */
export const t: Strings = DICTS[LANG];
